import type { TrainingPlannerPlan } from 'components/TrainingPlanner/types';
import { usePlannerPlans } from 'components/TrainingPlanner/useTrainingPlannerOptions';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Where a plan lives: `/club/{slug}/training-planner/plan/{planId}[/{page}]`. */
export const planRoute = (clubSlug: string, planId: number, page = '') =>
    `/club/${clubSlug}/training-planner/plan/${planId}${page ? `/${page}` : ''}`;

/** The module entry — the plan overview, and the fallback when no plan resolves. */
export const plannerRoute = (clubSlug: string) => `/club/${clubSlug}/training-planner`;

/**
 * Subpages that exist inside every plan, in sidebar order.
 *
 * Modal tools are not among them. Booking import and availability still answer
 * on their old URLs for the sake of bookmarks, but both open over a plan page
 * rather than becoming a place of their own.
 */
// In sidebar order, and it has to stay that way: `planNavItems` decides what
// the column looks like and this decides what a page key may be, so the two
// drifting apart makes one of them a second, silent opinion about the module's
// shape.
export const PLAN_PAGES = ['', 'groups', 'students', 'columns', 'tags', 'billing', 'revenue', 'trainers'] as const;
export type PlanPage = (typeof PLAN_PAGES)[number];

interface PlanScopeState {
    selectedByClub: Record<string, number>;
    createdByClub: Record<string, TrainingPlannerPlan>;
    select: (clubSlug: string, planId: number) => void;
    selectCreated: (clubSlug: string, plan: TrainingPlannerPlan) => void;
}

/**
 * The last plan the club worked in, per club.
 *
 * Since the plan moved into the route this store no longer decides anything —
 * it only remembers where to go when the route does not say, which is the
 * module entry and the legacy redirects. Keeping it means re-entering the
 * planner lands you where you left off instead of on a chooser every time.
 */
export const usePlanScopeStore = create<PlanScopeState>()(
    persist(
        (set) => ({
            selectedByClub: {},
            createdByClub: {},
            select: (clubSlug, planId) =>
                set((state) => ({
                    selectedByClub: { ...state.selectedByClub, [clubSlug]: planId },
                    createdByClub: Object.fromEntries(
                        Object.entries(state.createdByClub).filter(([slug]) => slug !== clubSlug)
                    )
                })),
            selectCreated: (clubSlug, plan) =>
                set((state) => ({
                    selectedByClub: { ...state.selectedByClub, [clubSlug]: plan.id },
                    createdByClub: { ...state.createdByClub, [clubSlug]: plan }
                }))
        }),
        {
            name: 'training-planner.plan-scope.v2',
            partialize: (state) => ({
                ...state,
                // The API becomes authoritative again after a reload. Only the
                // selected id needs to survive between browser sessions.
                createdByClub: {}
            })
        }
    )
);

/**
 * Which plan the planner is currently working in.
 *
 * The route wins over the store: a URL can be shared and opened twice, a
 * localStorage value cannot. Two plans in two tabs therefore stay independent,
 * which they could not while the store alone decided.
 *
 * Falls back to the first active plan whenever neither says anything usable. A
 * newly created plan is inserted into the SWR cache before revalidation so the
 * grid can switch to it immediately.
 */
export const usePlanScope = (clubSlug?: string) => {
    const { plans, isLoading, mutate } = usePlannerPlans(clubSlug);
    const { query, pathname, push, isReady } = useRouter();
    const storedId = usePlanScopeStore((state) => (clubSlug ? state.selectedByClub[clubSlug] : undefined));
    const newlyCreated = usePlanScopeStore((state) => (clubSlug ? state.createdByClub[clubSlug] : undefined));
    const selectInStore = usePlanScopeStore((state) => state.select);
    const selectCreatedInStore = usePlanScopeStore((state) => state.selectCreated);

    /**
     * None of the planner pages define getServerSideProps, so Next serves them
     * statically optimized: `query` is empty on the first client render and
     * only fills after hydration. Reading `planId` before `isReady` therefore
     * reports "no plan in the route" on a page whose entire purpose is a plan
     * in the route — and the store below would answer with whichever plan was
     * open last. `isPending` keeps that render on the loading state instead of
     * briefly rendering somebody else's plan under this URL's heading.
     */
    const isPending = !isReady;
    const routeId = isReady ? Number(query.planId) || undefined : undefined;
    const wantedId = routeId ?? storedId;

    const selected: TrainingPlannerPlan | undefined =
        plans.find((plan) => plan.id === wantedId) ??
        (newlyCreated?.id === wantedId ? newlyCreated : undefined) ??
        // A plan id in the route that the club does not have is not silently
        // swapped for another one — PlanShell turns that into a visible miss.
        (routeId ? undefined : (plans.find((plan) => !plan.is_archived) ?? plans[0]));

    // Walking into a plan makes it the one to return to.
    useEffect(() => {
        if (clubSlug && routeId && routeId !== storedId && plans.some((plan) => plan.id === routeId)) {
            selectInStore(clubSlug, routeId);
        }
    }, [clubSlug, plans, routeId, selectInStore, storedId]);

    /**
     * Switches plans without leaving the page you are on: the same subpage in
     * the other plan, because the reason to switch is almost always comparing
     * the same thing. Outside a plan route it only records the choice.
     */
    const select = useCallback(
        (planId: number) => {
            if (!clubSlug) return;

            selectInStore(clubSlug, planId);

            if (routeId) {
                const page = pathname.split('/[planId]/')[1] ?? '';
                void push(planRoute(clubSlug, planId, page));
            }
        },
        [clubSlug, pathname, push, routeId, selectInStore]
    );

    const selectCreated = useCallback(
        (created: TrainingPlannerPlan) => {
            if (!clubSlug) return;

            // Store the full resource as well as its id. The switcher and grid
            // can now show it before SWR has received the refreshed plan list.
            selectCreatedInStore(clubSlug, created);
            void mutate(
                (current) => ({
                    ...current,
                    data: [created, ...(current?.data ?? []).filter((plan) => plan.id !== created.id)]
                }),
                { revalidate: true }
            );
        },
        [clubSlug, mutate, selectCreatedInStore]
    );

    return {
        plans,
        plan: selected,
        planId: selected?.id,
        select,
        selectCreated,
        /** No plan exists yet — the club has to create one before it can plan. */
        isEmpty: !isPending && !isLoading && plans.length === 0,
        /** The route names a plan this club does not have (deleted, or someone else's). */
        isMissing: !isPending && !isLoading && !!routeId && !selected,
        // Covers the render before the router has parsed the URL as well as the
        // plans request, so no caller can act on a half-resolved scope.
        isLoading: isPending || isLoading,
        mutate
    };
};

export default usePlanScope;
